/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { Alert, Badge } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { EquipmentField, ValidationIssue, ValidationRow } from "../types";
import { updateCell } from "../dataImportWizardSlice";
import { useAppDispatch } from "pages/thermography/config/store";
import { ColumnSchema } from "../equipmentsSchema";
import { useTranslation } from "react-i18next";

type Props = {
    fileName: string;
    fileId: string;
    isValid: boolean;
    rowsToFix: ValidationRow[];
    issues: ValidationIssue[];
    columns: ColumnSchema[];
};

const cloneRow = (r: ValidationRow): ValidationRow => ({
    ...r,
    data: r.data ? { ...(r.data as any) } : r.data
});

export default function FileValidationSection(props: Props) {
    const { t } = useTranslation();
    const tt = (key: string) => t(key, { ns: "translation" });
    const dispatch = useAppDispatch();
    const { fileId, isValid, rowsToFix, issues, columns } = props;

    const tableWrapRef = useRef<HTMLDivElement | null>(null);
    const [editableRows, setEditableRows] = useState<ValidationRow[]>([]);

    useEffect(() => {
        setEditableRows(rowsToFix.map(cloneRow));
    }, [rowsToFix]);

    const severityBadge = (sev: string) =>
        sev === "error" ? <Badge bg="danger">{tt("pages.thermography.dataImportWizard.fileValidation.severity.error")}</Badge> : <Badge bg="warning">{tt("pages.thermography.dataImportWizard.fileValidation.severity.warning")}</Badge>;

    const editorText = (options: any) => (
        <InputText
            value={options.value ?? ""}
            onChange={(e) => options.editorCallback(e.target.value)}
            className="w-100"
        />
    );

    const onCellEditComplete = (e: any) => {
        const { rowData, field, newValue } = e;

        const normalizedField = (typeof field === "string" && field.startsWith("data."))
            ? field.replace("data.", "")
            : field;

        const finalField = normalizedField as EquipmentField;

        dispatch(updateCell({
            fileId,
            rowId: rowData.rowId,
            field: finalField,
            value: newValue
        }));
    };

    const jumpToRowId = (rowId: string) => {
        const wrap = tableWrapRef.current;
        if (!wrap) return;

        const tr = wrap.querySelector<HTMLTableRowElement>(`.fix-row-${rowId}`);
        if (!tr) return;

        tr.scrollIntoView({ behavior: "smooth", block: "center" });
        tr.classList.add("row-jump-highlight");
        window.setTimeout(() => tr.classList.remove("row-jump-highlight"), 900);
    };

    const hasTables = !isValid && editableRows.length > 0 && issues.length > 0;

    return (
        <div className="border rounded p-3 bg-body mb-3">
            {isValid && (
                <Alert variant="subtle-success" className="py-2 mb-0">
                    {tt("pages.thermography.dataImportWizard.fileValidation.successMessage")}
                </Alert>
            )}

            {hasTables && (
                <>
                    <Alert variant="subtle-warning" className="py-2">
                        {tt("pages.thermography.dataImportWizard.fileValidation.inconsistenciesMessagePrefix")} <b>{tt("pages.thermography.dataImportWizard.footer.validate")}</b> {tt("pages.thermography.dataImportWizard.fileValidation.inconsistenciesMessageSuffix")}
                    </Alert>

                    <div ref={tableWrapRef}>
                        <h6 className="mb-2">{tt("pages.thermography.dataImportWizard.fileValidation.recordsToFix")}</h6>

                        <DataTable
                            value={editableRows}
                            dataKey="rowId"
                            editMode="cell"
                            size="small"
                            stripedRows
                            showGridlines
                            rowClassName={(row) => `fix-row-${row.rowId}`}
                            className="mb-3"
                            scrollable
                            scrollHeight="420px"
                        >
                            <Column field="rowIndex" header={tt("pages.thermography.dataImportWizard.fileValidation.table.row")} style={{ width: 90 }} />

                            {columns.map(col => (
                                <Column
                                    key={col.field}
                                    field={`data.${col.field}`}
                                    header={col.header}
                                    style={col.width ? { width: col.width } : undefined}
                                    editor={col.editable ? editorText : undefined}
                                    onCellEditComplete={col.editable ? onCellEditComplete : undefined}
                                />
                            ))}
                        </DataTable>
                    </div>

                    <h6 className="mb-2">{tt("pages.thermography.dataImportWizard.fileValidation.issuesTitle")}</h6>
                    <DataTable
                        value={issues}
                        dataKey="id"
                        size="small"
                        stripedRows
                        showGridlines
                        onRowClick={(e) => jumpToRowId((e.data as ValidationIssue).rowId)}
                    >
                        <Column
                            field="rowIndex"
                            header={tt("pages.thermography.dataImportWizard.fileValidation.table.row")}
                            style={{ width: 90 }}
                            body={(r: ValidationIssue) => <span className="fw-semibold">{r.rowIndex}</span>}
                        />

                        <Column
                            header={tt("pages.thermography.dataImportWizard.fileValidation.table.level")}
                            body={(r: ValidationIssue) => severityBadge(r.severity)}
                            style={{ width: 110 }}
                        />
                        <Column field="message" header={tt("pages.thermography.dataImportWizard.fileValidation.table.message")} />
                        <Column field="field" header={tt("pages.thermography.dataImportWizard.fileValidation.table.field")} style={{ width: 180 }} />
                        <Column
                            header={tt("pages.thermography.dataImportWizard.fileValidation.table.status")}
                            body={(r: ValidationIssue) =>
                                r.resolved ? <Badge bg="success">{tt("pages.thermography.dataImportWizard.fileValidation.status.ok")}</Badge> : <Badge bg="danger">{tt("pages.thermography.dataImportWizard.fileValidation.status.pending")}</Badge>
                            }
                            style={{ width: 120 }}
                        />
                    </DataTable>
                </>
            )}
        </div>
    );
}
